import React, { useRef, useState } from 'react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/core'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import { Copy } from 'lucide-react'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { wrapBidi, stripBidi } from '../utils'
import useLocaleText from 'components/useLocaleText'

interface PasteIdsDialogProps {
  onConfirm(ids: string[]): void
}

export const PasteIdsDialog: React.FC<PasteIdsDialogProps> = ({ onConfirm }) => {
  const isRtl = useSelector(isRtlSelector)

  const { isOpen, onOpen, onClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const t_paste_ids = useLocaleText('paste_ids', true)
  const t_enter_ids = useLocaleText('enter_ids', true)
  const t_add = useLocaleText('add', true)
  const t_cancel = useLocaleText('cancel', true)

  const [raw, setRaw] = useState('')

  const handleConfirm = () => {
    const ids = raw
      .split(/[,\t\r\n]+/)
      .map((id) => id.trim())
      .filter(Boolean)
    onConfirm(ids)
    setRaw('')
    onClose()
  }

  return (
    <>
      <NoFocusButton size="sm" variantColor="teal" variant="outline" onClick={onOpen} style={{ columnGap: 4 }}>
        <Text>{t_paste_ids}</Text>
        <Copy size={18} />
      </NoFocusButton>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} size="sm" isCentered>
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader>{t_paste_ids}</AlertDialogHeader>

          <AlertDialogBody>
            <Textarea
              value={wrapBidi(raw)}
              onChange={(e) => setRaw(stripBidi((e.target as HTMLTextAreaElement).value))}
              onBlur={(e) => setRaw(stripBidi((e.target as HTMLTextAreaElement).value.trim()))}
              resize="none"
              dir="ltr"
              textAlign={isRtl ? 'right' : 'left'}
              style={{ unicodeBidi: 'plaintext' }}
              placeholder={t_enter_ids}
              _hover={{ borderColor: 'black' }}
              _focus={{ borderColor: 'black' }}
            />
          </AlertDialogBody>

          <AlertDialogFooter style={{ columnGap: 10 }}>
            <NoFocusButton borderRadius="5px" ref={cancelRef} onClick={onClose}>
              {t_cancel}
            </NoFocusButton>

            <NoFocusButton
              borderRadius="5px"
              onClick={handleConfirm}
              isDisabled={!raw.trim()}
              bg="teal.500"
              color="white"
            >
              {t_add}
            </NoFocusButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
